import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { accountingApi } from '@/api/accounting'
import { formatMoney } from '@/components/billing/formatMoney'
import { getErrorMessage } from '@/utils/error'
import type { IncomeBreakdown, IncomeGroupBy, IncomeReport, IncomeReportParams } from '@/types/accounting'

const GROUP_BY_OPTIONS: IncomeGroupBy[] = ['day', 'week', 'month']
const BREAKDOWN_OPTIONS: IncomeBreakdown[] = ['student', 'group', 'subscription', 'method']

export function IncomeTab() {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  const [from, setFrom] = useState<Dayjs>(dayjs().startOf('month'))
  const [to, setTo] = useState<Dayjs>(dayjs().endOf('month'))
  const [groupBy, setGroupBy] = useState<IncomeGroupBy>('day')
  const [breakdown, setBreakdown] = useState<IncomeBreakdown | ''>('')
  const [report, setReport] = useState<IncomeReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  const params = useMemo<IncomeReportParams>(
    () => ({
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
      group_by: groupBy,
      ...(breakdown ? { breakdown } : {}),
    }),
    [from, to, groupBy, breakdown],
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setReport(await accountingApi.incomeReport(params))
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [params, t])

  useEffect(() => {
    void load()
  }, [load])

  // F-15: средний доход на один период выбранной группировки — делим итог
  // на число календарных периодов диапазона (включая пустые), а не только
  // периодов с доходом: так среднее отражает реальный темп заработка.
  const averagePerBucket = useMemo(() => {
    if (!report) return null
    const fromDate = dayjs(report.from)
    const toDate = dayjs(report.to)
    const days = toDate.diff(fromDate, 'day') + 1
    if (days <= 0) return null
    const buckets =
      report.group_by === 'day'
        ? days
        : report.group_by === 'week'
          ? Math.max(1, Math.ceil(days / 7))
          : toDate.startOf('month').diff(fromDate.startOf('month'), 'month') + 1
    if (buckets <= 0) return null
    return (Number(report.totals.gross) / buckets).toFixed(2)
  }, [report])

  // F-14: компактные подписи значений на столбцах (12 500 → «12,5 тыс.»)
  const barLabelFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }),
    [i18n.language],
  )

  const handleExport = async () => {
    setDownloading(true)
    setError(null)
    try {
      const blob = await accountingApi.incomeReportCsv(params)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `income-report-${params.from}-${params.to}.csv`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setDownloading(false)
    }
  }

  const series = report?.series ?? []

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems={{ md: 'center' }}>
        <DatePicker
          label={t('accounting.from', 'С')}
          value={from}
          onChange={(d) => d && setFrom(d)}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label={t('accounting.to', 'По')}
          value={to}
          onChange={(d) => d && setTo(d)}
          slotProps={{ textField: { size: 'small' } }}
        />
        <TextField
          select
          size="small"
          label={t('accounting.income.groupBy', 'Группировка')}
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as IncomeGroupBy)}
          sx={{ minWidth: 140 }}
        >
          {GROUP_BY_OPTIONS.map((g) => (
            <MenuItem key={g} value={g}>
              {t(`accounting.groupBy.${g}`, g)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('accounting.income.breakdown', 'Разрез')}
          value={breakdown}
          onChange={(e) => setBreakdown(e.target.value as IncomeBreakdown | '')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t('accounting.income.noBreakdown', 'Без разреза')}</MenuItem>
          {BREAKDOWN_OPTIONS.map((b) => (
            <MenuItem key={b} value={b}>
              {t(`accounting.breakdown.${b}`, b)}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" onClick={handleExport} disabled={downloading || loading}>
          {t('accounting.income.exportCsv', 'Экспорт CSV')}
        </Button>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {t('accounting.income.total', 'Доход за период')}
          </Typography>
          {loading ? (
            <Skeleton width={140} height={44} />
          ) : (
            <>
              <Typography variant="h4" fontWeight={700}>
                {formatMoney(report?.totals.gross, report?.currency)}
              </Typography>
              {averagePerBucket !== null && report && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t(`accounting.income.avg_${report.group_by}`)}:{' '}
                  {formatMoney(averagePerBucket, report.currency)}
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('accounting.income.dynamics', 'Динамика по периодам')}
          </Typography>
          {loading ? (
            <Skeleton variant="rounded" height={300} />
          ) : series.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              {t('accounting.income.noData', 'Нет данных за выбранный период')}
            </Typography>
          ) : (
            <BarChart
              height={300}
              xAxis={[
                {
                  scaleType: 'band',
                  data: series.map((s) => s.period),
                  valueFormatter: (value: string) => dayjs(value).format('DD.MM'),
                },
              ]}
              series={[
                {
                  data: series.map((s) => Number(s.gross)),
                  label: t('accounting.income.gross', 'Доход'),
                },
              ]}
              barLabel={(item) =>
                item.value != null && item.value !== 0 ? barLabelFormatter.format(item.value) : null
              }
            />
          )}
        </CardContent>
      </Card>

      {breakdown && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t(`accounting.breakdown.${breakdown}`, breakdown)}
            </Typography>
            {loading ? (
              <Skeleton variant="rounded" height={160} />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('accounting.income.name', 'Наименование')}</TableCell>
                      <TableCell align="right">{t('accounting.income.gross', 'Доход')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report?.breakdown_rows ?? []).map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right">{formatMoney(row.gross, report?.currency)}</TableCell>
                      </TableRow>
                    ))}
                    {(report?.breakdown_rows ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>
                          {t('accounting.income.noData', 'Нет данных за выбранный период')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </LocalizationProvider>
  )
}
